import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { usePermission } from '../../../hooks/usePermission';

const HomeContent = () => {
  const { can } = usePermission();
  const [content, setContent] = useState({
    header: '',
    description: '',
    services: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageUploads, setImageUploads] = useState({});

  const canEdit = can('content.edit');

  useEffect(() => {
    fetchHomeContent();
  }, []);

  const fetchHomeContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/home-page-content`, {
        withCredentials: true
      });
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching home content:', error);
      alert('Error fetching home content');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (!canEdit) return;
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index, field, value) => {
    if (!canEdit) return;
    const updatedServices = [...content.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value
    };
    setContent(prev => ({
      ...prev,
      services: updatedServices
    }));
  };

  const handleImageUpload = (index, event) => {
    if (!canEdit) return;
    const file = event.target.files[0];
    if (file) {
      setImageUploads(prev => ({
        ...prev,
        [index]: file
      }));
    }
  };

  const addService = () => {
    if (!canEdit) {
      alert("You don't have permission to edit home content.");
      return;
    }
    setContent(prev => ({
      ...prev,
      services: [...prev.services, { title: '', image: '' }]
    }));
  };

  const removeService = (index) => {
    if (!canEdit) {
      alert("You don't have permission to edit home content.");
      return;
    }
    setContent(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const saveContent = async () => {
    if (!canEdit) {
      alert("You don't have permission to edit home content.");
      return;
    }

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('header', content.header);
      formData.append('description', content.description);
      
      // Send services as JSON string - this is the key fix
      formData.append('services', JSON.stringify(content.services));
      
      // Add image files to form data
      Object.entries(imageUploads).forEach(([index, file]) => {
        formData.append('service_images', file);
      });

      await axios.put(`${SERVER}/home-page-content`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });

      setImageUploads({});
      alert('Home content saved successfully!');
      fetchHomeContent(); // Refresh data
    } catch (error) {
      console.error('Error saving home content:', error);
      alert('Error saving home content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-600 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-3xl shadow-2xl p-8 animate-scaleIn">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <span className="text-3xl">🏠</span>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">Home Page Content</h2>
              <p className="text-blue-100">Manage your website's home page content</p>
            </div>
          </div>
          {canEdit && (
            <button 
              className="px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-white/30 flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={saveContent}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾 Save Changes</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </>
              )}
            </button>
          )}
          {!canEdit && (
            <div className="px-4 py-2 bg-yellow-500/20 backdrop-blur-sm rounded-xl border border-yellow-400/30">
              <span className="text-yellow-100 text-sm font-medium">👁️ View Only Mode</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Header Text Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-slate-200/50 animate-slideUp">
          <label htmlFor="header" className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
            <span className="text-xl">📝</span>
            Header Text
          </label>
          <input
            id="header"
            type="text"
            value={content.header}
            onChange={(e) => handleInputChange('header', e.target.value)}
            placeholder="Enter header text"
            className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed transition-all"
            disabled={!canEdit}
          />
        </div>

        {/* Description Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-slate-200/50 animate-slideUp" style={{ animationDelay: '0.1s' }}>
          <label htmlFor="description" className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
            <span className="text-xl">📄</span>
            Description
          </label>
          <textarea
            id="description"
            value={content.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter description"
            rows="4"
            className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed transition-all resize-none"
            disabled={!canEdit}
          />
        </div>

        {/* Services Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-slate-200/50 animate-slideUp" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-xl">⚙️</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-800">Services</h3>
            </div>
            {canEdit && (
              <button 
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2 group"
                onClick={addService}
              >
                <span>+ Add Service</span>
              </button>
            )}
          </div>

          <div className="space-y-6">
            {content.services.map((service, index) => (
              <div key={index} className="group border-2 border-slate-200 hover:border-blue-300 rounded-2xl p-6 bg-gradient-to-br from-slate-50 to-white transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <h4 className="text-lg font-bold text-slate-800">Service {index + 1}</h4>
                  </div>
                  {canEdit && (
                    <button 
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
                      onClick={() => removeService(index)}
                    >
                      <span>🗑️</span>
                      <span>Remove</span>
                    </button>
                  )}
                </div>

                <div className="space-y-5">
                  <div>
                    <label htmlFor={`service-title-${index}`} className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>📌</span>
                      Title
                    </label>
                    <input
                      id={`service-title-${index}`}
                      type="text"
                      value={service.title || ''}
                      onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                      placeholder="Service title"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed transition-all"
                      disabled={!canEdit}
                    />
                  </div>

                  <div>
                    <label htmlFor={`service-image-${index}`} className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>🖼️</span>
                      Image
                    </label>
                    {canEdit && (
                      <input
                        id={`service-image-${index}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(index, e)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:font-semibold transition-all"
                      />
                    )}
                    {service.image && (
                      <div className="mt-4 flex items-center gap-4">
                        <img 
                          src={`${SERVER}/${service.image}`} 
                          alt="Current service" 
                          className="w-32 h-32 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="flex-1">
                          <span className="block text-sm font-semibold text-slate-700">Current Image</span>
                          <span className="block text-xs text-slate-500 mt-1">Upload a new image to replace</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeContent;